import React from "react";
import { API_BASE_URL } from "../../../src/utils/api";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "N/A";
const fmtDt = (d) => d ? new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A";
const currency = (v) => v != null ? `$${parseFloat(v).toFixed(2)}` : "N/A";

const ViolationPrintContent = ({
  violationDetails,
  member,
  violationType,
  imageURL,
  allFiles = [],
  fines = [],
  letterLogs = [],
  statusHistory = [],
  visits = [],
}) => {
  if (!violationDetails) return null;

  const v = violationDetails;
  const ownerName = member
    ? `${member.FirstName || member.first_name || ""} ${member.LastName || member.last_name || ""}`.trim()
    : "N/A";
  const address = member?.FullAddress || member?.full_address || member?.AddressLine1 || "N/A";
  const email = member?.EmailAddress || member?.email || "N/A";
  const phone = member?.PhoneNumber || member?.phone || "N/A";
  const memberCode = v.property_id || "N/A";

  const imageFiles = allFiles.filter(f => f.file_type?.startsWith("image/"));
  const otherFiles = allFiles.filter(f => !f.file_type?.startsWith("image/"));

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", color: "#1a202c", margin: 0, padding: 0, fontSize: "11pt" }}>

      {/* ── HEADER ── */}
      <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2f63ad 100%)", color: "#fff", padding: "24px 32px 20px", borderRadius: "0 0 8px 8px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "9pt", letterSpacing: 2, textTransform: "uppercase", opacity: 0.75, marginBottom: 4 }}>Heritage Springs Community HOA</div>
            <div style={{ fontSize: "22pt", fontWeight: 700, marginBottom: 4 }}>Violation Report</div>
            <div style={{ fontSize: "13pt", opacity: 0.9 }}>Violation #{v.id} &nbsp;·&nbsp; {v.violation_id || ""}</div>
          </div>
          <div style={{ textAlign: "right", fontSize: "9pt", opacity: 0.85 }}>
            <div>Printed: {fmtDt(new Date())}</div>
            <div style={{ marginTop: 4 }}>My HOA Management System</div>
          </div>
        </div>

        {/* Status badge */}
        <div style={{ marginTop: 16 }}>
          <span style={{
            background: v.status === "Closed" ? "#22c55e" : v.status === "Pending" ? "#f59e0b" : v.status === "Open" ? "#ef4444" : "#6b7280",
            color: "#fff", padding: "4px 14px", borderRadius: 20, fontWeight: 700, fontSize: "10pt"
          }}>{v.status || "N/A"}</span>
        </div>
      </div>

      <div style={{ padding: "0 32px 32px" }}>

        {/* ── VIOLATION DETAILS ── */}
        <Section title="Violation Details">
          <Grid2Col>
            <Field label="Violation ID" value={v.violation_id || `#${v.id}`} />
            <Field label="Status" value={v.status} />
            <Field label="Type" value={violationType || "N/A"} />
            <Field label="CCR Article" value={v.ccr_article || "N/A"} />
            <Field label="Violated Date" value={fmt(v.violated_date)} />
            <Field label="Cure By Date" value={fmt(v.cure_by)} />
            <Field label="Created" value={fmtDt(v.created_at)} />
            <Field label="Assigned To" value={v.resolved_by_name || "Unassigned"} />
          </Grid2Col>
          {v.description && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, color: "#4a5568", marginBottom: 4, fontSize: "9pt", textTransform: "uppercase", letterSpacing: 1 }}>Description</div>
              <div style={{ background: "#f7f9fc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px", lineHeight: 1.6 }}>{v.description}</div>
            </div>
          )}
        </Section>

        {/* ── MEMBER / PROPERTY ── */}
        <Section title="Member & Property">
          <Grid2Col>
            <Field label="Member Code" value={memberCode} />
            <Field label="Owner Name" value={ownerName} />
            <Field label="Email" value={email} />
            <Field label="Phone" value={phone} />
            <Field label="Address" value={address} />
          </Grid2Col>
        </Section>

        {/* ── VIOLATION IMAGES ── */}
        {imageFiles.length > 0 && (
          <Section title="Violation Images">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {imageFiles.map((f, i) => (
                <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", maxWidth: 280 }}>
                  <img
                    src={`${API_BASE_URL}/violationsimages/${v.id}/${encodeURIComponent(f.file_name)}`}
                    alt={f.file_name}
                    style={{ width: "100%", display: "block", maxHeight: 200, objectFit: "cover" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div style={{ padding: "6px 10px", fontSize: "9pt", color: "#4a5568" }}>{f.file_name}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── OTHER ATTACHMENTS ── */}
        {otherFiles.length > 0 && (
          <Section title="Attachments">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <Th>File Name</Th><Th>Type</Th><Th>Uploaded</Th>
              </tr></thead>
              <tbody>
                {otherFiles.map((f, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{f.file_name}</Td>
                    <Td>{f.file_type || "N/A"}</Td>
                    <Td>{fmt(f.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* ── COMMENTS ── */}
        {v.comments?.length > 0 && (
          <Section title="Comments">
            {v.comments.map((c, i) => (
              <div key={i} style={{ borderLeft: "3px solid #2f63ad", paddingLeft: 12, marginBottom: 12, background: i % 2 === 0 ? "#f7f9fc" : "#fff", borderRadius: "0 6px 6px 0", padding: "8px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: "#2f63ad" }}>{c.user_name} {c.user_lastname}</span>
                  <span style={{ fontSize: "9pt", color: "#6b7280" }}>{fmtDt(c.sent_at)}</span>
                </div>
                <div style={{ color: "#1a202c", lineHeight: 1.5 }}>{c.comment_text}</div>
              </div>
            ))}
          </Section>
        )}

        {/* ── FINES ── */}
        {fines.length > 0 && (
          <Section title="Fines">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <Th>Fine Date</Th><Th>Amount</Th><Th>Status</Th><Th>Description</Th><Th>Billed</Th>
              </tr></thead>
              <tbody>
                {fines.map((f, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{fmt(f.fine_date)}</Td>
                    <Td>{currency(f.amount)}</Td>
                    <Td>{f.status || "N/A"}</Td>
                    <Td>{f.description || "N/A"}</Td>
                    <Td>{f.sent_to_billing ? "Yes" : "No"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: "right", marginTop: 8, fontWeight: 700 }}>
              Total: {currency(fines.reduce((s, f) => s + parseFloat(f.amount || 0), 0))}
            </div>
          </Section>
        )}

        {/* ── LETTER HISTORY ── */}
        {letterLogs.length > 0 && (
          <Section title="Letter History">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <Th>Date</Th><Th>Action</Th><Th>Recipient</Th><Th>Sent By</Th>
              </tr></thead>
              <tbody>
                {letterLogs.map((l, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{fmtDt(l.created_at || l.sent_at)}</Td>
                    <Td>{l.action || l.letter_action || "N/A"}</Td>
                    <Td>{l.recipient || l.letter_recipient || "N/A"}</Td>
                    <Td>{l.sent_by || l.letter_sent_by || "N/A"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* ── STATUS HISTORY ── */}
        {statusHistory.length > 0 && (
          <Section title="Status History">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <Th>Date</Th><Th>From</Th><Th>To</Th><Th>Changed By</Th><Th>Note</Th>
              </tr></thead>
              <tbody>
                {statusHistory.map((h, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{fmtDt(h.changed_at)}</Td>
                    <Td>{h.old_status || "N/A"}</Td>
                    <Td>{h.new_status || "N/A"}</Td>
                    <Td>{h.changed_by_name || h.changed_by || "N/A"}</Td>
                    <Td>{h.note || ""}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* ── FOOTER ── */}
        <div style={{ marginTop: 32, borderTop: "2px solid #e2e8f0", paddingTop: 12, display: "flex", justifyContent: "space-between", fontSize: "9pt", color: "#6b7280" }}>
          <span>Heritage Springs Community HOA | My HOA Management System</span>
          <span>Printed: {fmtDt(new Date())}</span>
        </div>
      </div>
    </div>
  );
};

// ── Helper components ──────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ background: "#2f63ad", width: 4, height: 18, borderRadius: 2 }} />
      <div style={{ fontWeight: 700, fontSize: "11pt", color: "#1e3a5f", textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
    </div>
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px" }}>
      {children}
    </div>
  </div>
);

const Grid2Col = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>{children}</div>
);

const Field = ({ label, value }) => (
  <div style={{ paddingBottom: 4 }}>
    <div style={{ fontSize: "8.5pt", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 1 }}>{label}</div>
    <div style={{ fontWeight: 600, color: "#1a202c" }}>{value || "N/A"}</div>
  </div>
);

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "10pt" };
const thRowStyle = { background: "#f0f4fa" };
const altRowStyle = { background: "#f7f9fc" };

const Th = ({ children }) => (
  <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: "#1e3a5f", fontSize: "9pt", borderBottom: "2px solid #2f63ad" }}>{children}</th>
);

const Td = ({ children }) => (
  <td style={{ padding: "7px 10px", borderBottom: "1px solid #e2e8f0", color: "#1a202c" }}>{children}</td>
);

export default ViolationPrintContent;
